import { Observable, of, defer, merge } from "rxjs"
import { switchMap, catchError, filter, delayWhen, map, pairwise, takeUntil } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { getHttpHeaders } from "../../../auth/selectors"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_VOIDS_REPORT_DEFAULT_FILTER,
    setVoidsReportDefaultFilter,
} from "../../../filters/actions"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customers/customersTable/epics"
import {
    CLEAR_VOIDS_REPORT_DATA,
    clearVoidsReportData,
    FETCH_VOIDS_LOG_REPORT_REQUESTED,
    FETCH_VOIDS_SUMMARY_REPORT_REQUESTED,
    fetchVoidsLogReportFailure,
    fetchVoidsLogReportRequested,
    fetchVoidsLogReportSuccess,
    fetchVoidsSummaryReportFailure,
    fetchVoidsSummaryReportRequested,
    fetchVoidsSummaryReportSuccess,
    VOIDS_REPORT_AGENDA_MOUNTED,
    voidsReportAgendaMounted,
} from "./actions"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { areAnyLocationsSelected } from "../../../filters/selectors"
import { fetchVoidsLogReport, fetchVoidsSummaryReport } from "./request"
import { isVoidsReportAgendaOpened } from "../../../data/selectors"
import { getSpecialPricingLogReportQueryApiParam } from "../specialPricing/selectors"
import { getVoidsReportQueryApiParam } from "./selectors"
import { Action, StateObject } from "../../../redux/types"
import { URL_CHANGED } from "../../../router/actions"
import { getCurrentPathQuery } from "../../../router/selectors"
import { getReportsStateObject } from "../selectors"
import { VoidsReportTabsEnum } from "./VoidsReportTabsContainer"

const getActiveTab = (state: StateObject) => getCurrentPathQuery(state).tab

const fetchActiveTabReportRequested = (state: StateObject) => {
    switch (getActiveTab(state)) {
        case VoidsReportTabsEnum.Log:
            return fetchVoidsLogReportRequested()
        default:
            return fetchVoidsSummaryReportRequested()
    }
}

const isActiveTabReportLoaded = (state: StateObject): boolean => {
    const reports = getReportsStateObject(state)
    switch (getActiveTab(state)) {
        case VoidsReportTabsEnum.Log:
            return reports.voidsLogs !== null
        default:
            return reports.voidsSummary !== null
    }
}

export const specialPricingReportAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(VOIDS_REPORT_AGENDA_MOUNTED),
        switchMap(() => {
            return [clearVoidsReportData(), setVoidsReportDefaultFilter()]
        })
    )

export const fetchVoidsReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isVoidsReportAgendaOpened(deps.getState())),
        switchMap(() => [voidsReportAgendaMounted()])
    )

export const fetchVoidsReportOnTabSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(URL_CHANGED),
        map(() => ({
            isOpened: isVoidsReportAgendaOpened(deps.getState()),
            tab: getActiveTab(deps.getState()),
        })),
        pairwise(),
        filter(([previous, current]) => previous.isOpened && current.isOpened && previous.tab !== current.tab),
        filter(() => !isActiveTabReportLoaded(deps.getState())),
        switchMap(() => [fetchActiveTabReportRequested(deps.getState())])
    )

export const voidsReportDefaultFilterChangedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_VOIDS_REPORT_DEFAULT_FILTER),
        filter(() => isVoidsReportAgendaOpened(deps.getState())),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => {
            return [clearVoidsReportData(), fetchActiveTabReportRequested(deps.getState())]
        })
    )

export const voidsReportFilterChanged = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "voids" || action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        ),
        actions$.pipe(ofType(REFETCH_DATA))
    ).pipe(
        filter(() => isVoidsReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return [clearVoidsReportData(), fetchActiveTabReportRequested(deps.getState())]
        })
    )

export const specialPricingsReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_VOIDS_LOG_REPORT_REQUESTED),
        filter(() => isVoidsReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("voids-log-report")])
    )

export const fetchVoidsLogReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_VOIDS_LOG_REPORT_REQUESTED),
        filter(() => isVoidsReportAgendaOpened(deps.getState())),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                const params = getVoidsReportQueryApiParam(deps.getState())
                return defer(() => fetchVoidsLogReport(getHttpHeaders(deps.getState()), params)).pipe(
                    switchMap(result => {
                        if (result) {
                            return [createLoadingEnded("voids-log-report"), fetchVoidsLogReportSuccess(result)]
                        }

                        return [fetchVoidsLogReportFailure(), createLoadingEnded("voids-log-report")]
                    }),
                    catchError(() => of(createLoadingEnded("voids-log-report"))),
                    takeUntil(actions$.pipe(ofType(CLEAR_VOIDS_REPORT_DATA)))
                )
            }

            return [clearVoidsReportData(), createLoadingEnded("voids-log-report")]
        })
    )

export const voidsSummaryReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_VOIDS_SUMMARY_REPORT_REQUESTED),
        filter(() => isVoidsReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("voids-summary-report")])
    )

export const fetchVoidsSummaryReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_VOIDS_SUMMARY_REPORT_REQUESTED),
        filter(() => isVoidsReportAgendaOpened(deps.getState())),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                const params = getSpecialPricingLogReportQueryApiParam(deps.getState())
                return defer(() => fetchVoidsSummaryReport(getHttpHeaders(deps.getState()), params)).pipe(
                    switchMap(result => {
                        if (result) {
                            return [createLoadingEnded("voids-summary-report"), fetchVoidsSummaryReportSuccess(result)]
                        }

                        return [fetchVoidsSummaryReportFailure(), createLoadingEnded("voids-summary-report")]
                    }),
                    catchError(() => of(createLoadingEnded("voids-summary-report"))),
                    takeUntil(actions$.pipe(ofType(CLEAR_VOIDS_REPORT_DATA)))
                )
            }

            return [clearVoidsReportData(), createLoadingEnded("voids-summary-report")]
        })
    )

export default [
    specialPricingReportAgendaMountedEpics,
    voidsReportDefaultFilterChangedEpics,
    specialPricingsReportLoadingStartedEpics,
    fetchVoidsLogReportRequestedEpics,
    fetchVoidsReportOnAccountSwitch,
    fetchVoidsReportOnTabSwitch,
    voidsReportFilterChanged,
    voidsSummaryReportLoadingStartedEpics,
    fetchVoidsSummaryReportRequestedEpics,
]
